/**
 * Text lookup in the signed-in person's language (FR-035, constitution 1.2.0, T076c). French is
 * a person's default; the pages before anyone signs in are always in English (owner, 2026-10-13).
 * The app sets the language from the person's profile (`app/src/i18n/LanguageProvider.tsx`) and
 * re-renders; screens just call `t()`.
 */
import { regionName } from '../domain/regions';
import { REGION_NONE } from '../domain/filters';
import { COUNTRIES } from '../domain/countries';
import { DEFAULT_LANGUAGE, type Language } from '../domain/profile';
import { en } from './en';
import { fr, type MessageKey } from './fr';

export type { MessageKey };

/** The sign-in pages' language, whoever opens them (owner, 2026-10-13). */
export const SIGNED_OUT_LANGUAGE: Language = 'en';

const CATALOGUES: Record<Language, Record<MessageKey, string>> = { fr, en };

let current: Language = DEFAULT_LANGUAGE;

/** Switches every later `t()` call to `language`, and tells the browser (for screen readers). */
export function setLanguage(language: Language): void {
  current = language;
  if (typeof document !== 'undefined') document.documentElement.lang = language;
}

export function getLanguage(): Language {
  return current;
}

/** Looks up a message in the current language. Throws on an unknown key. */
export function t(key: MessageKey): string {
  const message = CATALOGUES[current][key];
  if (message === undefined) {
    throw new Error(`Unknown message catalogue key: ${JSON.stringify(key)}`);
  }
  return message;
}

/** Looks up a message and fills its `{placeholders}`, e.g. `tf('cellar.of', { name })`. */
export function tf(key: MessageKey, values: Record<string, string>): string {
  return t(key).replace(/\{(\w+)\}/g, (placeholder, name: string) => values[name] ?? placeholder);
}

/** "1 bouteille" / "6 bouteilles", "1 bottle" / "6 bottles". */
export function bottleCount(count: number): string {
  return tf(count === 1 ? 'stock.one' : 'stock.other', { count: String(count) });
}

/** A price in whole euros: "12 €" in French, "€12" in English; never a decimal (owner, 2026-10-15). */
export function formatWholeEuros(cents: number): string {
  return new Intl.NumberFormat(current === 'fr' ? 'fr-FR' : 'en-GB', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(cents / 100));
}

/** "1 achetée" / "3 achetées", and the same for bottles drunk (the map, User Story 10). */
export function mapCount(kind: 'bought' | 'drunk', count: number): string {
  return tf(`map.${kind}.${count === 1 ? 'one' : 'other'}` as MessageKey, { count: String(count) });
}

/** A month's name, 1 = janvier / January. */
export function monthName(month: number): string {
  return t(`month.${month}` as MessageKey);
}

/**
 * A country's name from its ISO code (FR-004). French names come from the app's own list
 * (`domain/countries.ts`, unchanged); English names from the browser's built-in country names.
 */
export function countryLabel(code: string): string {
  if (current === 'fr') {
    return COUNTRIES.find((country) => country.code === code)?.name ?? code;
  }
  try {
    return new Intl.DisplayNames([current], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** A wine's region as shown: its name, or "Autre région" (owner, 2026-10-03). */
export function regionLabel(region: string): string {
  return regionName(region, t('wine.regionOther'));
}

/** Gemini's free allowance ran out (owner, 2026-10-03): for the day, for a minute, or unknown. */
export function quotaMessage(period?: 'day' | 'minute'): string {
  return t(
    period === 'day'
      ? 'assist.quotaDay'
      : period === 'minute'
        ? 'assist.quotaMinute'
        : 'assist.quota',
  );
}

/** A Région filter choice: the region's name, "Autre région", or "À compléter" for none yet. */
export function regionFilterLabel(region: string): string {
  return region === REGION_NONE ? t('wine.regionMissing') : regionLabel(region);
}

/** A day as "03/10/26" (dd/mm/yy in both languages), for the online rating's notes (2026-10-03). */
export function shortDay({ year, month, day }: { year: number; month: number; day: number }) {
  const two = (n: number) => String(n % 100).padStart(2, '0');
  return `${two(day)}/${two(month)}/${two(year)}`;
}

/** One online-rating note: "03/10/26 : Vivino 4,3 / 5". */
export function onlineRatingNote(note: {
  day: { year: number; month: number; day: number };
  site: string;
  score: number;
  scale: number;
}): string {
  return tf('rating.onlineNote', {
    day: shortDay(note.day),
    site: note.site,
    score: note.score.toLocaleString(getLanguage()),
    scale: String(note.scale),
  });
}

/**
 * The items in alphabetical order of their label in the current language, ignoring case and
 * accents: every drop-down of words (owner, 2026-10-04); lists of numbers keep their own order.
 */
export function sortedByLabel<T>(items: readonly T[], label: (item: T) => string): T[] {
  return [...items].sort((a, b) =>
    label(a).localeCompare(label(b), getLanguage(), { sensitivity: 'base' }),
  );
}
